<template>
  <div ref="turnstileWidget"></div>
</template>

<script>
// script load timeout, see https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/
const SCRIPT_TIMEOUT_MS = 10000
const CHALLENGE_TIMEOUT_MS = 15000

// Shared across instances so the script is only ever injected once at a time.
let scriptPromise = null

function loadScript () {
  if (window.turnstile) {
    return Promise.resolve()
  }
  if (scriptPromise) {
    return scriptPromise
  }

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    let timer = null
    const fail = (message) => {
      clearTimeout(timer)
      reject(new Error(message))
    }

    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.onload = () => {
      clearTimeout(timer)
      if (window.turnstile) {
        resolve()
      } else {
        fail('Turnstile API did not initialise')
      }
    }
    script.onerror = () => {
      script.remove()
      fail('Turnstile script failed to load')
    }
    // Some mobile networks and in-app browsers never fire onload or onerror.
    timer = setTimeout(() => fail('Turnstile script timed out'), SCRIPT_TIMEOUT_MS)

    document.head.appendChild(script)
  }).catch(error => {
    scriptPromise = null
    throw error
  })

  return scriptPromise
}

export default {
  name: 'TurnstileWidget',
  props: {
    // 'render' = challenge runs on load (default)
    // 'execute' = widget stays hidden and idle until ensureRunning()/executeWidget() is called
    execution: {
      type: String,
      default: 'render'
    },
    action: {
      type: String,
      default: 'login'
    }
  },
  data () {
    return {
      widgetId: null,
      failed: false,
      executeRequested: false,
      hasRun: false,
      watchdog: null
    }
  },
  methods: {
    loadTurnstile () {
      loadScript()
        .then(() => {
          if (!this._isDestroyed) {
            this.renderWidget()
          }
        })
        .catch(() => this.fail('script-load-failed'))
    },
    renderWidget () {
      if (!window.turnstile || !this.$refs.turnstileWidget || this.widgetId !== null) {
        return
      }
      try {
        this.widgetId = window.turnstile.render(this.$refs.turnstileWidget, {
          sitekey: process.env.VUE_APP_TURNSTILE_SITE_KEY || '#{Project.Ui.Turnstile.SiteKey}',
          theme: 'light',
          execution: this.execution,
          action: this.action,
          appearance: this.execution === 'execute' ? 'execute' : 'always',
          retry: 'auto',
          'refresh-expired': 'auto',
          'refresh-timeout': 'auto',
          callback: this.onVerify,
          'error-callback': this.onError,
          'expired-callback': this.onExpired,
          'timeout-callback': this.onTimeout,
          'unsupported-callback': this.onUnsupported,
          'before-interactive-callback': this.onBeforeInteractive,
          'after-interactive-callback': this.onAfterInteractive
        })
      } catch (error) {
        this.widgetId = null
        this.fail('widget-render-failed')
        return
      }
      this.hasRun = false
      if (this.execution === 'render') {
        this.startWatchdog()
      } else if (this.executeRequested) {
        this.executeWidget()
      }
    },
    // Run the challenge now. Safe to call at any time: if the script is still loading
    // it runs once the widget renders; if the widget has failed it is rebuilt first.
    executeWidget () {
      if (this.failed) {
        this.executeRequested = true
        this.retryVerification()
        return
      }
      if (!window.turnstile || this.widgetId === null) {
        this.executeRequested = true
        this.loadTurnstile()
        return
      }
      this.executeRequested = false
      if (this.hasRun) {
        window.turnstile.reset(this.widgetId)
      }
      this.hasRun = true
      window.turnstile.execute(this.widgetId)
      this.startWatchdog()
      this.$nextTick(() => window.dispatchEvent(new Event('resize')))
    },
    resetWidget () {
      if (window.turnstile && this.widgetId !== null && !this.failed) {
        window.turnstile.reset(this.widgetId)
        this.hasRun = false
        if (this.execution === 'render') {
          this.startWatchdog()
        } else {
          this.stopWatchdog()
        }
      } else {
        this.retryVerification()
      }
    },
    ensureRunning () {
      if (this.execution === 'execute') {
        this.executeWidget()
      } else if (this.failed || this.widgetId === null) {
        this.retryVerification()
      }
    },
    retryVerification () {
      this.failed = false
      this.$emit('retrying')
      this.removeWidget()
      this.loadTurnstile()
    },
    removeWidget () {
      this.stopWatchdog()
      if (window.turnstile && this.widgetId !== null) {
        try {
          window.turnstile.remove(this.widgetId)
        } catch (error) {
          // Explicit catch block to prevent throwing in try
        }
      }
      this.widgetId = null
    },
    startWatchdog () {
      this.stopWatchdog()
      this.watchdog = setTimeout(() => this.fail('challenge-timeout'), CHALLENGE_TIMEOUT_MS)
    },
    stopWatchdog () {
      clearTimeout(this.watchdog)
      this.watchdog = null
    },
    fail (code) {
      if (this._isDestroyed) {
        return
      }
      this.stopWatchdog()
      this.failed = true
      this.$emit('errored', code)
    },
    onVerify (token) {
      this.stopWatchdog()
      this.failed = false
      this.$emit('verified', token)
    },
    onError (code) {
      this.fail(code)
    },
    onExpired () {
      if (this.execution === 'render') {
        this.startWatchdog()
      }
      this.$emit('expired')
    },
    onTimeout () {
      if (this.execution === 'render') {
        this.startWatchdog()
      } else {
        this.stopWatchdog()
      }
      this.$emit('timedOut')
    },
    onUnsupported () {
      this.fail('unsupported-browser')
    },
    onBeforeInteractive () {
      this.stopWatchdog()
      this.$emit('interactiveStart')
    },
    onAfterInteractive () {
      this.startWatchdog()
      this.$emit('interactiveEnd')
    }
  },
  mounted () {
    this.loadTurnstile()
  },
  beforeDestroy () {
    this.removeWidget()
  }
}
</script>
